import { z } from "zod";
import { zEmail, zId, zIsoDate } from "./common.js";
import { zLanguage, type PublicLanguageInfo } from "./i18n.js";

/**
 * CT-RM-28: peer-to-peer fundraising. Staff run a P2P campaign (tied to a fund,
 * optionally a campaign and a donation page); supporters register a personal
 * fundraising page — optionally on a team — and gifts through the page are
 * credited to them. Public URLs:
 *   /p/:orgSlug/:campaignSlug                 campaign + leaderboards + register
 *   /p/:orgSlug/:campaignSlug/:pageSlug       a fundraiser's page
 *   /p/:orgSlug/:campaignSlug/team/:teamSlug  a team page
 *   /p/:orgSlug/:campaignSlug/manage          supporter manage (emailed link)
 *   /embed/f/:orgSlug/:campaignSlug[/:pageSlug]  framable embeds
 */

export const P2P_LINK_TTL_HOURS = 72;
export const P2P_SESSION_TTL_HOURS = 2;
/** hidden honeypot input on the public forms — humans leave it empty */
export const P2P_HONEYPOT_FIELD = "website";
/** URL segments a fundraiser page can't take (they're routes) */
export const P2P_RESERVED_SLUGS = ["manage", "team", "teams", "register", "embed", "donate", "request-link", "exchange", "me"] as const;
export const P2P_LEADERBOARD_SIZE = 10;

export const P2P_SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/;
export const zP2PSlug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(P2P_SLUG_RE, "Use lowercase letters, numbers and dashes (2–64 characters)");

/** turn a title into a URL slug candidate ("Jane's 5K!" → "janes-5k") */
export function p2pSlugify(text: string): string {
  const s = text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/g, "");
  return s.length >= 2 ? s : "";
}

export const P2P_CAMPAIGN_STATUSES = ["draft", "active", "closed"] as const;
export type P2PCampaignStatus = (typeof P2P_CAMPAIGN_STATUSES)[number];
export const P2P_PAGE_STATUSES = ["pending", "approved", "hidden"] as const;
export type P2PPageStatus = (typeof P2P_PAGE_STATUSES)[number];

/** fundraiser photos are linked, never uploaded here — https only */
const zPhotoUrl = z
  .string()
  .trim()
  .max(1000)
  .url()
  .refine((u) => u.startsWith("https://"), "Photo links must start with https://");
const zGoal = z.number().int().min(100).max(100_000_000_000).describe("cents");

// ---- staff ----

export const zUpsertP2PCampaign = z
  .object({
    name: z.string().trim().min(1).max(200),
    slug: zP2PSlug,
    description: z.string().trim().max(10_000).nullable().optional(),
    campaignId: zId.nullable().optional(),
    fundId: zId,
    donationPageId: zId.nullable().optional(),
    goalAmount: zGoal.nullable().optional(),
    defaultPageGoal: zGoal.nullable().optional(),
    startsOn: zIsoDate.nullable().optional(),
    endsOn: zIsoDate.nullable().optional(),
    status: z.enum(P2P_CAMPAIGN_STATUSES).default("draft"),
    teamsEnabled: z.boolean().default(false),
    requireApproval: z.boolean().default(false),
    /** CT-RM-32: donors may choose a monthly gift on fundraiser and team pages */
    allowMonthly: z.boolean().default(false),
    /** CT-RM-32: donor messages wait for staff approval before they show on a page */
    requireMessageApproval: z.boolean().default(false),
  })
  .refine((c) => !c.startsOn || !c.endsOn || c.endsOn >= c.startsOn, { message: "The end date must be on or after the start date", path: ["endsOn"] });
export type UpsertP2PCampaign = z.input<typeof zUpsertP2PCampaign>;

export const zModerateP2P = z.object({ status: z.enum(["approved", "hidden"]) });
export type ModerateP2P = z.infer<typeof zModerateP2P>;

/** CT-RM-32: staff hand a team to another fundraiser on that team */
export const zP2PChangeCaptain = z.object({ pageId: zId });
export type P2PChangeCaptain = z.infer<typeof zP2PChangeCaptain>;

export const P2P_MESSAGE_MAX = 500;
export const P2P_MESSAGE_STATUSES = ["pending", "approved", "hidden"] as const;
export type P2PMessageStatus = (typeof P2P_MESSAGE_STATUSES)[number];

export interface P2PTotals {
  raised: number;
  giftCount: number;
  donorCount: number;
}

export interface P2PCampaignRow extends P2PTotals {
  id: string;
  name: string;
  slug: string;
  status: P2PCampaignStatus;
  goalAmount: number | null;
  startsOn: string | null;
  endsOn: string | null;
  teamsEnabled: boolean;
  requireApproval: boolean;
  allowMonthly: boolean;
  requireMessageApproval: boolean;
  fundraiserCount: number;
  teamCount: number;
  pendingCount: number;
  /** CT-RM-32: donor messages waiting for approval */
  pendingMessageCount: number;
}

export interface P2PFundraiserRow {
  id: string;
  title: string;
  slug: string;
  status: P2PPageStatus;
  constituentId: string;
  fundraiserName: string;
  /** staff-only (never on a public payload) */
  email: string | null;
  teamId: string | null;
  teamName: string | null;
  isCaptain: boolean;
  goalAmount: number | null;
  raised: number;
  giftCount: number;
  createdAt: string;
}

export interface P2PTeamRow {
  id: string;
  name: string;
  slug: string;
  status: P2PPageStatus;
  captainConstituentId: string | null;
  captainName: string | null;
  goalAmount: number | null;
  raised: number;
  giftCount: number;
  memberCount: number;
}

export interface P2PCampaignDetail extends P2PCampaignRow {
  description: string | null;
  campaignId: string | null;
  campaignName: string | null;
  fundId: string;
  fundName: string | null;
  donationPageId: string | null;
  donationPageName: string | null;
  defaultPageGoal: number | null;
  fundraisers: P2PFundraiserRow[];
  teams: P2PTeamRow[];
  /** CT-RM-32: donor messages left on the campaign's pages (newest first) */
  messages: P2PMessageRow[];
}

/** CT-RM-32: a donor message for staff moderation (staff see the real donor name) */
export interface P2PMessageRow {
  giftId: string;
  message: string;
  status: P2PMessageStatus;
  donorName: string;
  constituentId: string;
  anonymous: boolean;
  amount: number;
  monthly: boolean;
  date: string;
  pageTitle: string | null;
  teamName: string | null;
}

// ---- public: registration, manage link, donations ----

export const zP2PTeamChoice = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("none") }),
  z.object({ mode: z.literal("join"), teamId: zId }),
  z.object({ mode: z.literal("create"), name: z.string().trim().min(2).max(120), goalAmount: zGoal.nullable().optional() }),
]);
export type P2PTeamChoice = z.infer<typeof zP2PTeamChoice>;

export const zP2PRegister = z.object({
  /** CT-RM-30: the page language; a new constituent gets it as their preferred language */
  language: zLanguage.optional(),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: zEmail,
  title: z.string().trim().min(2).max(140),
  story: z.string().trim().max(10_000).optional(),
  goalAmount: zGoal.nullable().optional(),
  photoUrl: zPhotoUrl.or(z.literal("")).optional(),
  /** preferred page URL segment; derived from the name when omitted */
  slug: zP2PSlug.or(z.literal("")).optional(),
  team: zP2PTeamChoice.default({ mode: "none" }),
  [P2P_HONEYPOT_FIELD]: z.string().max(500).optional(),
});
export type P2PRegister = z.input<typeof zP2PRegister>;

export interface P2PRegisterResult {
  status: "created";
  pageSlug: string;
  teamSlug: string | null;
  /** the page waits for staff approval before it's public */
  pendingApproval: boolean;
}

export const zP2PRequestLink = z.object({ email: zEmail });
export const zP2PExchange = z.object({ token: z.string().min(20).max(400) });

export const zP2PUpdatePage = z.object({
  title: z.string().trim().min(2).max(140).optional(),
  story: z.string().trim().max(10_000).nullable().optional(),
  goalAmount: zGoal.nullable().optional(),
  photoUrl: zPhotoUrl.nullable().or(z.literal("")).optional(),
  /** captains only: their team's story and goal */
  team: z
    .object({
      story: z.string().trim().max(10_000).nullable().optional(),
      goalAmount: zGoal.nullable().optional(),
    })
    .optional(),
});
export type P2PUpdatePage = z.infer<typeof zP2PUpdatePage>;

export interface P2PSelfService {
  organizationName: string;
  currency: string;
  campaign: { name: string; slug: string };
  page: {
    title: string;
    slug: string;
    story: string | null;
    goalAmount: number | null;
    photoUrl: string | null;
    status: P2PPageStatus;
    raised: number;
    giftCount: number;
  };
  team: { name: string; slug: string; isCaptain: boolean; story: string | null; goalAmount: number | null } | null;
}

export const zP2PDonation = z.object({
  /** CT-RM-30: the page language; a new constituent gets it as their preferred language */
  language: zLanguage.optional(),
  amount: z.number().int().min(100).max(100_000_000),
  donor: z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: zEmail,
  }),
  /** hide the donor's name (and amount) on the page */
  anonymous: z.boolean().default(false),
  /** CT-RM-32: start a monthly gift (only when the campaign allows monthly) */
  monthly: z.boolean().default(false),
  /** CT-RM-32: optional message shown with the gift on the page (moderated) */
  message: z.string().trim().max(P2P_MESSAGE_MAX).optional(),
  /** dev/test payment adapter token ("tok_fail" declines); card data never reaches the API */
  paymentToken: z.string().min(1).max(500).default("checkout"),
  [P2P_HONEYPOT_FIELD]: z.string().max(500).optional(),
});
export type P2PDonation = z.input<typeof zP2PDonation>;

export interface P2PDonationResult {
  status: "thank_you";
  receiptNumber: string | null;
  amountCharged: number;
  /** CT-RM-32: a monthly gift was started */
  monthly?: boolean;
  /** CT-RM-32: the message waits for staff approval before it shows */
  messagePending?: boolean;
}

// ---- public read models: names only, never an email ----

export interface PublicP2PLeader {
  slug: string;
  title: string;
  fundraiserName: string;
  photoUrl: string | null;
  teamName: string | null;
  goalAmount: number | null;
  raised: number;
}

export interface PublicP2PTeamLeader {
  slug: string;
  name: string;
  goalAmount: number | null;
  raised: number;
  memberCount: number;
}

export interface PublicP2PDonor {
  /** "Anonymous", or first name + last initial */
  name: string;
  /** null when the donor gave anonymously */
  amount: number | null;
  date: string;
  /** CT-RM-32: the donor's approved message, if any */
  message: string | null;
}

export interface PublicP2PCampaignInfo {
  name: string;
  slug: string;
  description: string | null;
  goalAmount: number | null;
  startsOn: string | null;
  endsOn: string | null;
  teamsEnabled: boolean;
  registrationOpen: boolean;
  givingOpen: boolean;
  defaultPageGoal: number | null;
  /** CT-RM-32: the donate form offers a monthly gift */
  allowMonthly: boolean;
}

export interface PublicP2PCampaign extends PublicLanguageInfo {
  organizationName: string;
  currency: string;
  campaign: PublicP2PCampaignInfo & { raised: number; donorCount: number; fundraiserCount: number };
  topFundraisers: PublicP2PLeader[];
  topTeams: PublicP2PTeamLeader[];
  /** approved teams a new fundraiser can join */
  teams: { id: string; name: string; slug: string }[];
}

export interface PublicP2PPage extends PublicLanguageInfo {
  organizationName: string;
  currency: string;
  campaign: PublicP2PCampaignInfo;
  page: {
    title: string;
    slug: string;
    story: string | null;
    photoUrl: string | null;
    fundraiserName: string;
    goalAmount: number | null;
    raised: number;
    donorCount: number;
    team: { name: string; slug: string } | null;
  };
  recentDonors: PublicP2PDonor[];
  suggestedAmounts: number[];
  paymentMode: "simulated" | "unavailable";
}

export interface PublicP2PTeam extends PublicLanguageInfo {
  organizationName: string;
  currency: string;
  campaign: PublicP2PCampaignInfo;
  team: {
    name: string;
    slug: string;
    story: string | null;
    captainName: string | null;
    goalAmount: number | null;
    raised: number;
    donorCount: number;
  };
  members: PublicP2PLeader[];
  recentDonors: PublicP2PDonor[];
  suggestedAmounts: number[];
  paymentMode: "simulated" | "unavailable";
}

export const P2P_DEFAULT_AMOUNTS = [2500, 5000, 10000, 25000];
